import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import Layout from '../component/Layout';
import './JREManagement.css';

const JREManagement = () => {
  const [jreRegistrations, setJreRegistrations] = useState([]);
  const [jreStatusList, setJreStatusList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedJre, setSelectedJre] = useState(null);
  const [formData, setFormData] = useState({
    maxValue: '',
    role: 'sellerside',
    courior: false
  });

  // Fetch all JRE registrations with debug logging
  const fetchJreRegistrations = async () => {
    try {
      console.log('Fetching JRE registrations...');
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'jreregistrations'));
      const registrations = [];

      console.log('Total documents found:', querySnapshot.size);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Document ID:', doc.id, 'Data:', data);
        registrations.push({
          id: doc.id,
          ...data
        });
      });

      setJreRegistrations(registrations);
      console.log('JRE Registrations set:', registrations);
    } catch (error) {
      console.error('Error fetching JRE registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all JRE status
  const fetchJreStatus = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'jreStatus'));
      const statusList = [];
      querySnapshot.forEach((doc) => {
        statusList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      setJreStatusList(statusList);
      console.log('JRE Status set:', statusList);
    } catch (error) {
      console.error('Error fetching JRE status:', error);
    }
  };

  useEffect(() => {
    fetchJreRegistrations();
    fetchJreStatus();
  }, []);

  // Handle JRE selection
  const handleSelectJre = (jre) => {
    setSelectedJre(jre);
    // Check if status already exists for this JRE
    const existingStatus = jreStatusList.find(status => status.id === jre.jreID);
    setFormData({
      maxValue: existingStatus?.maxValue || '',
      role: existingStatus?.role || 'sellerside',
      courior: existingStatus?.courior || false
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxValue' ? parseFloat(value) || 0 : value
    }));
  };

  // Create or Update JRE status
  // Create or Update JRE status
  const handleUpdateStatus = async () => {
    if (!selectedJre) return;

    try {
      setUpdating(true);

      let operatorId = null;
      console.log('Selected JRE:', selectedJre.OpertorNumber);
      // 🧭 Step 1: Find operator document using operatorNumber in JRE data
      if (selectedJre.OpertorNumber) {
        try {
          const operatorRef = doc(db, 'operator', selectedJre.OpertorNumber);
          const operatorSnap = await getDoc(operatorRef);

          if (operatorSnap.exists()) {
            const operatorData = operatorSnap.data();
            operatorId = operatorData.operatorId || operatorData.operatorID || null;

            console.log('✅ Found operator ID:', operatorId);
          } else {
            console.warn('⚠️ No operator found for ID:', selectedJre.OpertorNumber);
          }
        } catch (opErr) {
          console.error('Error fetching operator document:', opErr);
        }
      } else {
        console.warn('⚠️ No operatorNumber in selected JRE');
      }

      // 🧾 Step 2: Prepare data for update
      const statusData = {
        maxValue: parseFloat(formData.maxValue) || 0,
        role: formData.role,
        courior: formData.courior === "true" || formData.courior === true,
        jreID: selectedJre.jreID,
        operatorId: operatorId, // 🆕 Added operatorId field
      };

      console.log('Updating JRE status:', selectedJre.jreID, statusData);

      // 🧩 Step 3: Save into Firestore
      const statusRef = doc(db, 'jreStatus', selectedJre.jreID);
      await setDoc(statusRef, statusData, { merge: true });

      alert('✅ JRE status updated successfully!');
      setSelectedJre(null);
      setFormData({
        maxValue: '',
        role: 'sellerside',
        courior: false,
      });
      const jreProfileRef = doc(db, "profile", selectedJre.jreID);
      await updateDoc(jreProfileRef, { jreDocs: true }, { merge: true });
      // Refresh status list
      fetchJreStatus();
    } catch (error) {
      console.error('❌ Error updating JRE status:', error);
      alert('Error updating JRE status: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };


  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate();
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get status for a JRE
  const getJreStatus = (jreID) => {
    return jreStatusList.find(status => status.id === jreID);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading JRE data...</div>
      </div>
    );
  }

  return (
    <Layout pageTitle="JRE Management" >
      <div className="jre-management-container">


        {/* JRE Registrations List */}
        <div className="jre-grid">
          {jreRegistrations.length > 0 ? jreRegistrations.map((jre) => {
            const status = getJreStatus(jre.jreID);
            return (
              <div
                key={jre.id}
                className={`jre-card ${selectedJre?.id === jre.id ? 'selected' : ''} ${status ? 'has-status' : 'no-status'
                  }`}
                onClick={() => handleSelectJre(jre)}
              >
                <div className="jre-card-header">
                  <h3 className="jre-id">{jre.jreID || 'No JRE ID'}</h3>

                  <div className={`status-indicator ${status ? 'active' : 'inactive'}`}>
                    {status ? 'Configured' : 'Not Configured'}
                  </div>
                </div>
                <h5>Operator Contact:{jre.OpertorNumber}</h5>
                <div className="jre-info">
                  <div className="info-row">
                    <span className="info-label">Name:</span>
                    <span className="info-value">
                      {jre.fullName || 'N/A'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Father's Name:</span>
                    <span className="info-value">{jre.fathersName || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Mobile:</span>
                    <span className="info-value">{jre.primaryMobile || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">City:</span>
                    <span className="info-value">{jre.city || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Address:</span>
                    <span className="info-value address">{jre.address || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Blood Group:</span>
                    <span className="info-value blood-group">{jre.bloodGroup || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Languages:</span>
                    <span className="info-value">
                      {jre.languages?.join(', ') || 'N/A'}
                    </span>
                  </div>

                  {/* Status Information */}
                  {status && (
                    <div className="status-info">
                      <div className="info-row">
                        <span className="info-label">Max Value:</span>
                        <span className="info-value highlight">{status.maxValue}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Role:</span>
                        <span className={`role-tag ${status.role}`}>
                          {status.role}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Last Updated:</span>
                        <span className="info-value small">
                          {formatTimestamp(status.updatedAt)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          }) : (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No JRE Registrations Found</h3>
              <p>There are no JRE registrations in the database.</p>
              <button
                onClick={fetchJreRegistrations}
                className="btn-primary retry-btn"
              >
                Retry Fetch
              </button>
            </div>
          )}
        </div>

        {/* Update Form */}
        {selectedJre && (
          <div className="update-form-overlay">
            <div className="update-form-container">
              <h2 className="form-title">
                Configure JRE: {selectedJre.jreID}
              </h2>
              <p className="form-subtitle">
                {selectedJre.firstName} {selectedJre.lastName} - {selectedJre.city}
              </p>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Max Value</label>
                  <input
                    type="number"
                    name="maxValue"
                    value={formData.maxValue}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter max value"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="sellerside">Seller Side</option>
                    <option value="buyerside">Buyer Side</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Courier</label>
                  <select
                    name="courior"
                    value={formData.courior}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button
                  onClick={handleUpdateStatus}
                  disabled={updating || !formData.maxValue}
                  className="btn-primary"
                >
                  {updating ? 'Updating...' : 'Save Configuration'}
                </button>
                <button
                  onClick={() => setSelectedJre(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default JREManagement;